import {
  CompsListingsSearchCriteria,
  GeoSearch,
  MILES_TO_METERS,
  SubjectData,
} from '@aloft/toolkit-shared';

import { InitialCompSearch } from '../types.ts';
import { filterHasValue } from './filterHasValue.ts';
import { DEFAULT_RECOMMENDED_COMP_PROPERTY_SUB_TYPE } from './getDefaultRecommendedCompSearch.ts';

const replaceUndefinedWithEmptyString = <T extends Record<string, any>>(obj: T): T => {
  const newObj: Record<string, any> = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] === undefined) {
      newObj[key] = '';
    } else {
      newObj[key] = obj[key];
    }
  });
  return newObj as T;
};

const mergeFilterField = <T extends Record<string, unknown>>(
  existing: T | undefined,
  defaultFilter: T | undefined,
): T => {
  const existingNormalized = replaceUndefinedWithEmptyString(existing ?? ({} as T));
  if (filterHasValue(existingNormalized)) {
    return existingNormalized;
  }
  return replaceUndefinedWithEmptyString(defaultFilter ?? ({} as T));
};

export const loadExistingSearchCriteria = (
  existingSearchCriteria: CompsListingsSearchCriteria,
  subject: SubjectData,
  getDefaultFormValues: () => InitialCompSearch['body']['search'],
): InitialCompSearch['body']['search'] => {
  const defaultSearch = getDefaultFormValues();
  const existingFilters = existingSearchCriteria.filters;
  const subjectCoords = {
    lon: subject.editableData.propertyIdentification.coordinates?.longitude || 0,
    lat: subject.editableData.propertyIdentification.coordinates?.latitude || 0,
  };
  let geoSearch: GeoSearch;
  if (existingSearchCriteria.coordinates && existingSearchCriteria.coordinates.length > 2) {
    geoSearch = {
      t: 'polygon',
      coords: existingSearchCriteria.coordinates.map((coordinate) => [
        coordinate.longitude,
        coordinate.latitude,
      ]),
    };
  } else {
    geoSearch = {
      t: 'radius',
      radiusInMeters: existingSearchCriteria.radius
        ? existingSearchCriteria.radius * MILES_TO_METERS
        : defaultSearch.geoSearch.t === 'radius'
          ? defaultSearch.geoSearch.radiusInMeters
          : MILES_TO_METERS,
      coord: [subjectCoords.lon, subjectCoords.lat],
    };
  }
  const search = {
    filters: {
      ...defaultSearch.filters,
      ...existingFilters,
      status: existingFilters?.status?.trim() ?? '',
      propertySubType:
        existingFilters?.propertySubType?.trim() || DEFAULT_RECOMMENDED_COMP_PROPERTY_SUB_TYPE,
      saleDate: mergeFilterField(existingFilters?.saleDate, defaultSearch.filters?.saleDate),
      salePrice: mergeFilterField(existingFilters?.salePrice, defaultSearch.filters?.salePrice),
      gla: mergeFilterField(existingFilters?.gla, defaultSearch.filters?.gla),
      lotSize: mergeFilterField(existingFilters?.lotSize, defaultSearch.filters?.lotSize),
      yearBuilt: mergeFilterField(existingFilters?.yearBuilt, defaultSearch.filters?.yearBuilt),
      bedrooms: mergeFilterField(existingFilters?.bedrooms, defaultSearch.filters?.bedrooms),
      fullBaths: mergeFilterField(existingFilters?.fullBaths, defaultSearch.filters?.fullBaths),
      halfBaths: mergeFilterField(existingFilters?.halfBaths, defaultSearch.filters?.halfBaths),
      stories: mergeFilterField(existingFilters?.stories, defaultSearch.filters?.stories),
      garageSpaces: mergeFilterField(
        existingFilters?.garageSpaces,
        defaultSearch.filters?.garageSpaces,
      ),
    },
    geoSearch,
    matchCriteria: existingSearchCriteria.matchCriteria || defaultSearch.matchCriteria,
  };

  return search as InitialCompSearch['body']['search'];
};
